import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../reduxStore/store';
import {
  deleteLocalStorage,
  deserify,
  getMediaBasePath,
  IClassAndSubjectSelected,
  Loader,
  NoContentCard,
  setLocalStorage,
  useCommonServiceClientContext,
  QrCodeUrlTypeEnum,
  getTeacherSubjectEnum,
  getLocalStorage,
  PAL_BASE_URL,
} from '@geneo2-web/shared-ui';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import {
  ASSESSMENT_CURRENT,
  ENTER_UID,
  FA_ASSIGN_SETTINGS,
  FA_QUESTION_BANK,
  FORMATIVE,
  HOME,
  LESSON_PLAN_LISTS,
  TEACH_CHAPTER_SELECTION,
  TEACH_TOPIC_SELECTION,
  TEACHING_FLOW,
} from '../../routeHandling/RoutesNomenclature';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { setClassAndSubjectInfo } from '../Home/reducer/homeDashboard.slice';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  setChapterSubjectInfo,
  setChapterTopicInfo,
  setLessonPlanRedirectionPath,
  setResourceRedirectionPath,
  setSelectedTopic,
} from '../Teach/reducer/teach.slice';
import { Timestamp } from '@bufbuild/protobuf';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
  ModeOfConductionEnum,
  SessionModeEnum,
  SessionStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { Code, ConnectError } from '@bufbuild/connect';
import { onResourceClick } from '../../utils/resource';
import {
  setAssessmentCreationType,
  setCreatedAssessmentStatus,
  setFAQuestionModuleMap,
  setFetchedAssessmentDetails,
  setModuleFilteredQuestions,
  setQuestionsCount,
  setSelectedTasksInfo,
} from '../Assessment/reducer/assessmentFlow.slice';
import { convertToSequenceInfo } from '../Assessment/Common/ReviewAssessment/functions';

const DEFAULT_QR_ERROR = 'Error Occurred. Invalid QR Code.';

const QR_NAVIGATION_RESOLVE_PATH =
  '/public/qr_code.service.v1.QRNavigationService/ResolveQRCodeNavigation';

function getErrorDisplayMessage(error: unknown, fallback: string): string {
  if (error instanceof ConnectError) {
    const raw = error.rawMessage?.trim();
    if (raw) {
      return raw;
    }
    const msg = error.message?.trim();
    if (msg) {
      return msg;
    }
    return fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

async function postResolveQrNavigation(qrCode: string): Promise<unknown> {
  const baseUrl = process.env.NX_GENEO_BACKEND_URL || '';
  const res = await fetch(
    `${PAL_BASE_URL && PAL_BASE_URL.startsWith('http') ? '' : baseUrl}${
      PAL_BASE_URL || ''
    }${QR_NAVIGATION_RESOLVE_PATH}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qrCode }),
    }
  );
  if (!res.ok) {
    throw new Error('Failed to resolve QR code navigation');
  }
  return res.json();
}

export const ScreenResolver = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const teacher_id = getLocalStorage('userId');
  const searchParams = new URLSearchParams(location.search);
  const qrId = searchParams.get('QRId');
  // const moduleId = searchParams.get('moduleId');
  // const sectionId = searchParams.get('sectionId');
  // const subjectId = searchParams.get('subjectId');

  const { user_info } = deserify(
    useAppSelector((state: RootState) => state.auth)
  );
  const isLoggedIn = !!user_info;

  const [loading, setLoading] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const {
    ContentCommonAPIServiceV2ClientWithStatusCodeHandler,
    LessonTeachAPIServiceV1ClientWithStatusCodeHandler,
    UMSLoginAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const teacherSubjectsList: IClassAndSubjectSelected[] =
    user_info?.teachClassSubjects.flatMap((classInfo) =>
      classInfo.subjects.map((subject) => ({
        classname: classInfo.className,
        section: classInfo.sectionName,
        sectionId: classInfo.sectionId,
        subjectId: subject.subjectId,
        subject: subject.subjectName,
        icon: subject.iconUrl
          ? getMediaBasePath(subject.iconUrl, 'schoolnetStaticAssetBucket')
          : '',
        color: subject.textColor,
        bookId: subject.bookId,
        classId: classInfo.classId,
        isClickerSupported: classInfo.isClickerSupported ? true : false,
      }))
    ) || [];

  // ----------------------------------------------------

  async function fetchSubjectChapterInfo(
    subjectId: string,
    classSubjectInfo?: IClassAndSubjectSelected
  ) {
    const response =
      await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchSubjectChapterInfoV2(
        {
          personId: user_info?.teacherProfileId,
          personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
          subjectId: Number(subjectId),
          bookId: classSubjectInfo?.bookId,
          sectionId: classSubjectInfo?.sectionId,
          isAssessFlow: false,
        }
      );
    if (response?.data) {
      dispatch(setChapterSubjectInfo(response.data));
    }
  }

  // ----------------------------------------------------

  const getAndSetSubject = async (subjectId: string) => {
    try {
      const foundClassSubject = teacherSubjectsList?.find(
        (subject) => subject.subjectId === Number(subjectId)
      );
      if (foundClassSubject) {
        dispatch(setClassAndSubjectInfo(foundClassSubject));
        return foundClassSubject;
      }
      throw new Error('Subject not found');
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Subject not found');
    }
  };

  // ----------------------------------------------------

  const setSubjectAndChapterInfo = async (subjectId: string) => {
    const foundClassSubject = await getAndSetSubject(subjectId);
    await fetchSubjectChapterInfo(subjectId, foundClassSubject);
  };

  // ----------------------------------------------------

  const setSubjectChapterAndTopicInfo = async (
    subjectId: string,
    chapterId: string,
    topicId: string
  ) => {
    const foundClassSubject = await getAndSetSubject(subjectId);
    await fetchSubjectChapterInfo(subjectId, foundClassSubject);
    const response =
      await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterTopicInfoV2(
        {
          personId: Number(user_info?.teacherProfileId),
          personType: 'TEACHER',
          subjectId: Number(subjectId),
          chapterId: Number(chapterId),
          sectionId: foundClassSubject.sectionId,
          isAssessFlow: false,
          bookId: foundClassSubject.bookId,
        }
      );
    const data = response.data;
    data?.topics.sort((a, b) => a.topicNo - b.topicNo);
    dispatch(setChapterTopicInfo(data));
    const topics = data?.topics || [];
    const foundTopic = topics.find(
      (topic) => topic.topicId === Number(topicId)
    );
    if (foundTopic) {
      dispatch(setSelectedTopic(foundTopic));
    }
  };

  // ----------------------------------------------------

  const setLessonPlanInfoAndRedirect = async (
    subjectId: string,
    chapterId: string,
    topicId: string,
    lessonId: string
  ) => {
    const foundClassSubject = await getAndSetSubject(subjectId);
    const response =
      await LessonTeachAPIServiceV1ClientWithStatusCodeHandler.getPreviousLessonSessionInfo(
        {
          teacherId: user_info?.teacherProfileId,
          lessonId,
        }
      );

    const newSessionInfo =
      await LessonTeachAPIServiceV1ClientWithStatusCodeHandler.createTeacherLessonSession(
        {
          teacherId: user_info?.teacherProfileId,
          lessonId,
          schoolId: user_info?.schoolDetails[0]?.schoolId,
          academicYear: 0,
          classId: foundClassSubject?.classId,
          section: foundClassSubject.section,
          subject: getTeacherSubjectEnum(
            Number(foundClassSubject.subjectId),
            user_info?.teachClassSubjects
          ),
          sessionResourceIds: response.data?.completedResourceIds,
          startTime: Timestamp.fromDate(new Date()),
          sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
          sessionMode: SessionModeEnum.SESSION_MODE_TEACH,
        }
      );
    dispatch(setLessonPlanRedirectionPath(HOME));
    navigate({
      pathname: `${TEACHING_FLOW}/${subjectId}/${chapterId}/${topicId}/${lessonId}`,
      search: `?${createSearchParams({
        lessonSessionId: newSessionInfo.teacherLessonSessionId.toString(),
      })}`,
    });
  };

  // ----------------------------------------------------

  const getDataFromQRId = async () => {
    try {
      if (!isLoggedIn) {
        navigate(ENTER_UID);
        return;
      }

      if (!qrId) {
        navigate(HOME);
        return;
      }

      setLoading('loading');
      setErrorMessage(null);

      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchTeacherProfile(
          {
            teacherProfileId: user_info?.teacherProfileId,
          }
        );

      const data = (await postResolveQrNavigation(qrId)) as {
        qrCodeNavigation?: {
          chapterId?: number;
          courseId?: number;
          subjectId?: number;
          topicId?: number;
          lessonPlanId?: string;
          resourceId?: string;
          assessmentId?: string;
          urlType?: QrCodeUrlTypeEnum;
        };
      };

      const nav = data?.qrCodeNavigation;
      const chapterId = nav?.chapterId;
      const subjectId = nav?.courseId ?? nav?.subjectId;
      const topicId = nav?.topicId;
      const lessonPlanId = nav?.lessonPlanId;
      const resourceId = nav?.resourceId;

      if (isNaN(Number(subjectId))) {
        throw new Error(
          'Failed to resolve QR code navigation - Invalid subject'
        );
      }

      const subjectIdStr = String(subjectId);

      if (nav?.urlType === QrCodeUrlTypeEnum.SUBJECT) {
        await getAndSetSubject(subjectIdStr);
        navigate(`${TEACH_CHAPTER_SELECTION}/${subjectId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.CHAPTER) {
        if (isNaN(Number(chapterId))) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter'
          );
        }
        await setSubjectAndChapterInfo(subjectIdStr);
        navigate(`${TEACH_TOPIC_SELECTION}/${subjectId}/${chapterId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.TOPIC) {
        if (isNaN(Number(chapterId)) && !isNaN(Number(topicId))) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter or topic'
          );
        }
        await setSubjectChapterAndTopicInfo(
          subjectIdStr,
          String(chapterId),
          String(topicId)
        );
        navigate(`${LESSON_PLAN_LISTS}/${subjectId}/${chapterId}/${topicId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.LESSON_PLAN) {
        if (
          isNaN(Number(chapterId)) &&
          !isNaN(Number(topicId)) &&
          !lessonPlanId
        ) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter, topic, or Lesson Plan'
          );
        }
        if (!lessonPlanId) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid lesson plan'
          );
        }
        await setLessonPlanInfoAndRedirect(
          subjectIdStr,
          String(chapterId),
          String(topicId),
          lessonPlanId
        );
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.RESOURCE) {
        if (!resourceId) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid resource mapping'
          );
        }
        dispatch(setResourceRedirectionPath(HOME));
        await onResourceClick(
          navigate,
          {
            resourceId,
            subjectId: subjectId ? Number(subjectId) : undefined,
            chapterId: chapterId ? Number(chapterId) : undefined,
            topicId: topicId ? Number(topicId) : undefined,
            lessonId: lessonPlanId,
          },
          lessonPlanId
            ? SessionModeEnum.SESSION_MODE_LESSON_RESOURCE
            : SessionModeEnum.SESSION_MODE_TEACH
        );
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.ASSESSMENT) {
        await getAndSetSubject(subjectIdStr);
        fetchAssessmentContent(nav?.assessmentId, nav?.chapterId);
      }

      deleteLocalStorage('qrId');
      return;
    } catch (error) {
      if (
        error instanceof ConnectError &&
        error.code === Code.Unauthenticated
      ) {
        setLocalStorage('qrId', qrId);
      } else {
        deleteLocalStorage('qrId');
      }
      console.log(error);
      setErrorMessage(getErrorDisplayMessage(error, DEFAULT_QR_ERROR));
      setLoading('error');
    }
  };

  // ----------------------------------------------------

  const fetchAssessmentContent = async (
    assessmentId?: string,
    chapterId?: number
  ) => {
    if (!assessmentId) return;
    if (!chapterId) return;
    if (!isLoggedIn) {
      navigate(ENTER_UID);
      return;
    }

    if (!qrId) {
      navigate(HOME);
      return;
    }

    try {
      setLoading('loading');
      const fetchAssessmentContentResponse =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: Number(assessmentId),
          }
        );

      if (fetchAssessmentContentResponse.data) {
        setLoading('idle');
        const assessmentData = fetchAssessmentContentResponse.data;
        const mode_of_conduction =
          assessmentData?.assessment?.modeOfConduction ||
          ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP;
        dispatch(
          setAssessmentCreationType({
            asessmentType: assessmentData?.assessment?.taskType,
            modeOfConduction: assessmentData?.assessment?.modeOfConduction,
            creationType: [
              AssessmentEnum.ASSESSMENT_HOMEWORK.toString(),
              AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT.toString(),
              AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT.toString(),
            ].includes(
              assessmentData.assessment?.assessmentType?.toString() ?? ''
            )
              ? 'Readymade'
              : undefined,
            markingScheme:
              mode_of_conduction ===
              ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
                ? MarkingSchemeEnum.MARKING_SCHEME_GENEO
                : assessmentData?.assessment?.markingSchemeType ===
                  MarkingSchemeEnum.MARKING_SCHEME_JEE
                ? MarkingSchemeEnum.MARKING_SCHEME_JEE
                : MarkingSchemeEnum.MARKING_SCHEME_GENEO,
          })
        );

        switch (assessmentData?.assessment?.taskType) {
          case TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_AUTOMATED:
          case TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM:
            navigate(`${FA_ASSIGN_SETTINGS}?assessment_id=${assessmentId}`, {
              replace: true,
            });
            break;

          case TaskEnum.TASK_FORMATIVE_ASSESSMENT_AUTOMATED:
          case TaskEnum.TASK_FORMATIVE_ASSESSMENT_CUSTOM:
            dispatch(
              setCreatedAssessmentStatus(
                assessmentData.assessment?.taskCreationStatus
              )
            );
            dispatch(setFAQuestionModuleMap(assessmentData.questionModuleMap));
            dispatch(
              setModuleFilteredQuestions({
                questions: assessmentData.assessmentContent,
              })
            );
            dispatch(setFetchedAssessmentDetails(assessmentData.assessment));
            const sequenceInfo = convertToSequenceInfo(
              assessmentData.questionsSequenceInfo
            );
            if (sequenceInfo) {
              dispatch(setSelectedTasksInfo(sequenceInfo));
            }
            dispatch(setQuestionsCount());
            dispatch(
              setAssessmentCreationType({
                asessmentType: AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT,
                modeOfConduction: assessmentData.assessment.modeOfConduction,
                creationType: 'Readymade',
                markingScheme: assessmentData.assessment.markingSchemeType,
              })
            );
            navigate(
              `${FORMATIVE}${ASSESSMENT_CURRENT}/${
                assessmentData.assessment.moduleId || chapterId
              }`,
              { replace: true }
            );
            break;

          default:
            break;
        }
      } else {
        setLoading('error');
      }
    } catch (error) {
      console.log(error);
      setLoading('error');
    }
  };

  // ----------------------------------------------------

  useEffect(() => {
    if (qrId) {
      console.log('setting qrId in local storage:', qrId);
      setLocalStorage('qrId', qrId);
    }

    getDataFromQRId();

    // if (moduleId && sectionId && subjectId) {
    //   navigate(
    //     `${FA_QUESTION_BANK}?moduleId=${moduleId}&sectionId=${sectionId}&subjectId=${subjectId}`
    //   );
    // } else getDataFromQRId();
  }, []);

  // ----------------------------------------------------

  if (loading === 'loading') {
    return <Loader />;
  }

  if (loading === 'error') {
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text={errorMessage ?? DEFAULT_QR_ERROR}
      />
    );
  }

  return <Box>Redirecting to the requested page...</Box>;
};
